import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuthState } from 'state/useAuthState';
import { useMapPlacesState } from 'state/useMapPlacesState';
import { Place } from 'features/places/places.types';
import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import {
  MapPlacesAPIData,
  UsePlacesProps,
  UsePlacesReturn,
} from './usePlaces.types';
import {
  fetchMapPlaces,
  MAP_PLACES_QUERY_KEY,
  MAP_PLACES_VERSION,
  updateMapPlaces,
} from './usePlaces.utils';

type UseApiPlacesProps = UsePlacesProps & {
  enabled: boolean;
};

type UseApiPlacesReturn = UsePlacesReturn & {
  saveMultipleMapPlacesToAPI: (placeLocations: Place[]) => Promise<void>;
};

type PlacesUpdate = (places: Place[]) => Place[];

const prependNewPlaces = (newPlaces: Place[], places: Place[]): Place[] => {
  const placeIds = new Set(places.map((place) => place.id));
  const unsavedPlaces = newPlaces.filter((place) => !placeIds.has(place.id));
  return unsavedPlaces.length ? unsavedPlaces.concat(places) : places;
};

const buildMapPlacesData = (places: Place[]): MapPlacesAPIData => ({
  version: MAP_PLACES_VERSION,
  places,
  updatedAt: Date.now(),
});

/**
 * Places come from local state when logged out and only from the API when logged in.
 * Local places from before login are merged into the API by `useMigrateLocalPlaces`.
 */
export const useApiPlaces = ({
  sync = false,
  enabled,
}: UseApiPlacesProps): UseApiPlacesReturn => {
  const { isAuthenticated, showMembershipFeatures } = useAuthState();
  const {
    places: localPlaces,
    addMapPlace: localAddMapPlace,
    deleteMapPlace: localDeleteMapPlace,
    updateMapPlace: localUpdateMapPlace,
  } = useMapPlacesState();

  // Persisted so places still show on a cold start or while offline
  const query = useQuery(
    {
      queryKey: MAP_PLACES_QUERY_KEY,
      queryFn: fetchMapPlaces,
      staleTime: 1000 * 60 * 30, // 30 minutes
      refetchOnMount: sync ? 'always' : true,
      enabled: enabled && isAuthenticated,
    },
    persistedQueryClient,
  );

  // Each save is applied to a fresh copy of the API places, so saves made on
  // other devices since the last fetch aren't overwritten.
  // Saves use `networkMode: 'always'` so they fail right away when offline,
  // rather than pausing and silently running once back online.
  const { mutateAsync } = useMutation(
    {
      networkMode: 'always',
      mutationFn: async (applyUpdate: PlacesUpdate) => {
        const latest = await persistedQueryClient.fetchQuery({
          queryKey: MAP_PLACES_QUERY_KEY,
          queryFn: fetchMapPlaces,
          staleTime: 0,
          networkMode: 'always',
          // The user can retry by saving again
          retry: false,
        });
        const latestPlaces = latest?.places ?? [];
        const updatedPlaces = applyUpdate(latestPlaces);
        if (updatedPlaces === latestPlaces) return;

        const update = buildMapPlacesData(updatedPlaces);
        await updateMapPlaces(update);
        // Lets callers use the saved places (e.g. open the place drawer) right
        // away instead of waiting for the refetch
        persistedQueryClient.setQueryData(MAP_PLACES_QUERY_KEY, update);
      },
      // Not returned, so callers don't wait on the refetch
      onSettled: () => {
        persistedQueryClient.invalidateQueries({
          queryKey: MAP_PLACES_QUERY_KEY,
        });
      },
    },
    persistedQueryClient,
  );

  const apiPlaces = useMemo(() => query.data?.places ?? [], [query.data]);
  const places = isAuthenticated ? apiPlaces : localPlaces;

  const addMapPlace = useCallback(
    async (placeLocation: Place) => {
      if (!isAuthenticated) {
        localAddMapPlace(placeLocation);
        return;
      }
      await mutateAsync((latest) => [placeLocation].concat(latest));
    },
    [isAuthenticated, localAddMapPlace, mutateAsync],
  );

  const saveMultipleMapPlacesToAPI = useCallback(
    async (placeLocations: Place[]) => {
      if (!isAuthenticated) {
        throw new Error('saveMultipleMapPlacesToAPI requires a logged-in user');
      }
      await mutateAsync((latest) => prependNewPlaces(placeLocations, latest));
    },
    [isAuthenticated, mutateAsync],
  );

  const deleteMapPlace = useCallback(
    async (placeId: number) => {
      if (!isAuthenticated) {
        localDeleteMapPlace(placeId);
        return;
      }
      await mutateAsync((latest) =>
        latest.filter((place) => place.id !== placeId),
      );
    },
    [isAuthenticated, localDeleteMapPlace, mutateAsync],
  );

  const updateMapPlace = useCallback(
    async (placeLocation: Place) => {
      if (!isAuthenticated) {
        localUpdateMapPlace(placeLocation);
        return;
      }
      await mutateAsync((latest) =>
        latest.map((place) =>
          place.id === placeLocation.id ? placeLocation : place,
        ),
      );
    },
    [isAuthenticated, localUpdateMapPlace, mutateAsync],
  );

  return {
    places,
    loading: query.isLoading,
    addMapPlace,
    saveMultipleMapPlacesToAPI,
    deleteMapPlace,
    updateMapPlace,
    canAddPlace: places.length === 0 || showMembershipFeatures,
  };
};
